import React, { Dispatch, useEffect, useRef, useState } from 'react'
import {
  Box,
  Avatar,
  Card,
  CardContent,
  Grid2 as Grid,
  styled,
  Badge,
  IconButton,
  CircularProgress,
  Alert,
  Link
} from '@mui/material'
import Storage from 'storage/auth'
import InputBase from 'design-system/input/base'
import { InputMask } from 'components/InputMask'
import { IUserSession } from 'types'
import SelectBox from 'components/SelectBox'
import ButtonBase from 'design-system/button'
import { patchImage, patchUser } from 'services/user'
import { NavGoBack } from 'components/NavLinkBack'
import EditIcon from '@mui/icons-material/Edit'
import { brazilStates } from 'utils/countryState'
import { useToast } from 'services/context/toast'
import { docMasks } from 'utils/masks'
import { CustomListItemButton } from 'components/ListItemButton'
import { sendEmailPasswordReset } from 'services/auth'
import ContentModal from 'components/ContentModal'
import { trimStringInJSON } from 'utils/formatString'
import {
  EmailOutlined,
  HelpOutline,
  InfoOutlined,
  LaunchOutlined,
  LockOpenOutlined,
  GavelOutlined
} from '@mui/icons-material'
import { INFO_SA_EMAIL } from 'consts'
import NavLinkBase from 'design-system/NavLink'
import { AboutUsModal } from './components/AboutUsModal'
import { FAQ, LEGAL_INFORMATION } from 'navigation/CONSTANTS'
import ConfirmationModal from 'components/ConfirmationModal'
import { adjustColorForTheme } from 'styles/utils'

const StyledCard = styled(Card)(() => ({
  borderRadius: '16px'
}))

type UserKeys = keyof IUserSession

const formKeys: Array<UserKeys> = [
  'first_name',
  'last_name',
  'treatment',
  'professional_license',
  'professional_license_number',
  'specialization_name',
  'specialization_number',
  'mobile_phone'
]

const ProfilePage: React.FC = () => {
  const user = Storage.user.get()
  const toast = useToast()
  const [userState, setUserState] = useState<IUserSession>({
    ...user,
    professional_license_number: user?.professional_license_number?.slice(0, -2)
  } as IUserSession)
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false)
  const [imageState, setImageState] = useState<IStateStdTypes>('idle')
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [isUpdatePasswordDialogOpen, setIsUpdatePasswordDialogOpen] =
    useState(false)
  const [isAboutUsModalOpen, setIsAboutUsModalOpen] = useState(false)

  const [countryState, setCountryState] = useState(
    user?.professional_license_number?.slice(-2) ?? ''
  )

  const countryStateController: [any, Dispatch<any>] = [
    countryState,
    setCountryState
  ]

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errors, setErrors] = useState<Map<UserKeys, string>>(new Map())

  const getFinalData = (): IUserSession => {
    return trimStringInJSON({
      professional_id: userState.professional_id,
      first_name: userState.first_name,
      last_name: userState.last_name,
      treatment: userState.treatment,
      professional_license: userState.professional_license,
      professional_license_number: `${userState.professional_license_number}${countryStateController[0]}`,
      specialization_name: userState.specialization_name,
      specialization_number: userState.specialization_number,
      mobile_phone: userState.mobile_phone?.replaceAll(/[^\d]/g, '')
    })
  }

  const checkErrors = (data: IUserSession): Map<UserKeys, string> => {
    errors.clear()

    const newErrors = new Map<UserKeys, string>()

    const {
      first_name,
      last_name,
      professional_license,
      professional_license_number,
      specialization_number,
      mobile_phone
    } = data

    if (first_name.length < 3) {
      newErrors.set('first_name', 'Nome inválido')
    }
    if (last_name != '' && last_name.length < 2) {
      newErrors.set('last_name', 'Sobrenome inválido')
    }
    if (
      !docMasks[professional_license ?? ''].regexp.test(
        professional_license_number ?? ''
      )
    ) {
      newErrors.set(
        'professional_license_number',
        `${professional_license} inválido`
      )
    }
    if (mobile_phone?.length !== 11) {
      newErrors.set('mobile_phone', 'Número de telefone inválido')
    }
    if (!!specialization_number && (specialization_number?.length ?? 0) < 3) {
      newErrors.set(
        'specialization_number',
        'Número de especialização inválido'
      )
    }

    errors.clear()

    setErrors(newErrors)
    return newErrors
  }

  const updateProfessional = async () => {
    setState('loading')

    const formatData = getFinalData()

    const errors = checkErrors(formatData)

    if (errors.size > 0) {
      setState('error')
      return
    }

    const response = await patchUser(userState.professional_id, formatData)

    if (response.success) {
      for (const key in userState) {
        const currentKey = key as UserKeys
        if (formKeys.includes(currentKey)) continue

        formatData[currentKey] = userState[currentKey] as never
      }
      Storage.user.set(formatData)

      window.dispatchEvent(new Event('userUpdated'))

      toast.handler({
        content: 'Dados salvos com sucesso.',
        severity: 'success'
      })

      setState('success')
    } else {
      setState('error')
    }
  }

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setImageState('loading')
    const file = event.target.files?.[0]
    if (file) {
      const formData = new FormData()
      formData.append('image', file)
      formData.append('current_path', userState.user_image ?? '')

      const response = await patchImage(userState.professional_id, formData)

      if (response.success) {
        setImageState('success')
        if (user) {
          user.user_image = response.data?.url
          Storage.user.set(user)

          window.dispatchEvent(new Event('userImageUpdated'))
        }
        setUserState({
          ...userState,
          user_image: response.data?.url
        })
      } else {
        setImageState('error')
      }
    } else {
      setImageState('error')
    }
  }

  const change = (key: keyof IUserSession, value: any) => {
    const newData: IUserSession = {
      ...userState,
      [key]: value
    }

    if (key == 'professional_license') {
      newData.professional_license_number = ''
    }

    setUserState(newData)
  }

  const handleConfirmUpdatePwd = () => {
    sendEmailPasswordReset({ email: userState.email! })
    setOpenConfirmModal(false)
    setIsUpdatePasswordDialogOpen(true)
  }

  useEffect(() => {
    const newData = getFinalData()

    let isEdited = false
    for (const key of formKeys)
      if (newData[key] !== (user as IUserSession)[key]) {
        isEdited = true
        break
      }

    setIsEdited(isEdited)
  }, [userState, countryStateController[0]])

  useEffect(() => {
    const handleStorageChange = () => {
      const storageUser = Storage.user.get()
      setUserState({
        ...storageUser,
        professional_license_number:
          storageUser?.professional_license_number?.slice(0, -2)
      } as IUserSession)
    }

    window.addEventListener('userUpdated', handleStorageChange)

    return () => {
      window.removeEventListener('userUpdated', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    if (imageState === 'error') {
      toast.handler({
        content:
          'Ocorreu um erro ao tentar atualizar a foto de perfil, tente novamente.',
        severity: 'error'
      })
    }
  }, [imageState])

  return (
    <Box sx={{ padding: 2, maxWidth: 1200, margin: '0 auto' }}>
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavGoBack />
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <StyledCard>
          <CardContent>
            <Box display='flex' justifyContent='center' mb={2}>
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              <Badge
                overlap='circular'
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <IconButton
                    onClick={() => {
                      if (imageState !== 'loading')
                        fileInputRef.current?.click()
                    }}
                    sx={{
                      backgroundColor: 'background.paper',
                      width: 40,
                      height: 40,
                      boxShadow: 2,
                      '&:hover':
                        imageState === 'loading'
                          ? undefined
                          : {
                              backgroundColor: theme =>
                                adjustColorForTheme(
                                  theme.palette.mode,
                                  theme.palette.defaultComponent.main,
                                  0.2
                                )
                            },
                      cursor: imageState !== 'loading' ? 'auto' : undefined
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                }
              >
                <Box position='relative' display='inline-block'>
                  <Avatar
                    alt={userState?.first_name}
                    src={userState?.user_image}
                    sx={{ width: 160, height: 160 }}
                  />
                  {imageState === 'loading' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.5)', // Fundo semi-transparente
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%'
                      }}
                    >
                      <CircularProgress size={60} color='inherit' />
                    </Box>
                  )}
                </Box>
              </Badge>
            </Box>
            <Grid container spacing={2} columns={12}>
              <Grid size={12}>
                <Grid size={{ md: 3, sm: 4, xs: 12 }}>
                  <SelectBox
                    label='Tratamento'
                    menuItems={[
                      { label: 'Dr', value: 'Dr' },
                      { label: 'Dra', value: 'Dra' },
                      { label: 'Sr', value: 'Sr' },
                      { label: 'Sra', value: 'Sra' }
                    ]}
                    required
                    control={[
                      userState.treatment,
                      value => change('treatment', value)
                    ]}
                  />
                </Grid>
              </Grid>
              <Grid size={{ sm: 6, xs: 12 }}>
                <InputBase
                  label='Nome'
                  required
                  variant='outlined'
                  value={userState.first_name}
                  onChange={e => change('first_name', e.target.value)}
                  error={errors.has('first_name')}
                  helperText={errors.get('first_name')}
                />
              </Grid>
              <Grid size={{ sm: 6, xs: 12 }}>
                <InputBase
                  label='Sobrenome'
                  required
                  variant='outlined'
                  value={userState.last_name}
                  onChange={e => change('last_name', e.target.value)}
                  error={errors.has('last_name')}
                  helperText={errors.get('last_name')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <SelectBox
                  label='Tipo de documento'
                  required
                  menuItems={[
                    { label: 'CRM', value: 'CRM' },
                    { label: 'COREN', value: 'COREN' },
                    { label: 'CREFITO', value: 'CREFITO' },
                    { label: 'NA', value: 'NA' }
                  ]}
                  control={[
                    userState.professional_license,
                    value => change('professional_license', value)
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                {userState.professional_license &&
                  userState.professional_license != 'NA' && (
                    <SelectBox
                      label='Estado'
                      required={userState.professional_license != 'NA'}
                      disabled={userState.professional_license == 'NA'}
                      control={countryStateController}
                      menuItems={brazilStates.map(item => ({
                        label: item.acronym,
                        value: item.acronym
                      }))}
                    />
                  )}
              </Grid>
              <Grid size={{ xs: 12, sm: 5, md: 7 }}>
                {userState.professional_license &&
                  userState.professional_license != 'NA' && (
                    <InputMask
                      mask={docMasks[userState.professional_license ?? ''].mask}
                      label={`Digite seu ${userState.professional_license}`}
                      required={userState.professional_license != 'NA'}
                      disabled={userState.professional_license == 'NA'}
                      value={userState.professional_license_number}
                      toUpperCase
                      onChange={(_, value) =>
                        change('professional_license_number', value)
                      }
                      error={errors.has('professional_license_number')}
                      helperText={errors.get('professional_license_number')}
                    />
                  )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <InputBase
                  label='Nome da especialização'
                  variant='outlined'
                  value={userState.specialization_name}
                  onChange={e => change('specialization_name', e.target.value)}
                  error={errors.has('specialization_name')}
                  helperText={errors.get('specialization_name')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputMask
                  mask='00000'
                  label='Número da especialização'
                  value={userState.specialization_number}
                  onChange={(_, value) =>
                    change('specialization_number', value)
                  }
                  error={errors.has('specialization_number')}
                  helperText={errors.get('specialization_number')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputMask
                  mask='(00) 00000-0000'
                  value={userState.mobile_phone}
                  label='Telefone'
                  required
                  placeholder='(99) 99999-9999'
                  onChange={(_, value) => {
                    change('mobile_phone', value)
                  }}
                  error={errors.has('mobile_phone')}
                  helperText={errors.get('mobile_phone')}
                />
              </Grid>
              <Grid size={12} justifyContent={'end'} container>
                {state === 'error' && (
                  <Alert severity='error' style={{ width: '100%' }}>
                    {errors.size > 0
                      ? 'Alguns campos estão com erro.'
                      : 'Houve um erro ao tentar atualizar seus dados. Tente novamente.'}
                  </Alert>
                )}

                <ButtonBase
                  variant='contained'
                  type='submit'
                  loading={state == 'loading' || imageState == 'loading'}
                  disabled={
                    //!isEdited ||
                    state == 'loading' || imageState == 'loading'
                  }
                  onClick={updateProfessional}
                >
                  Salvar
                </ButtonBase>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>
        <ContentModal
          open={isUpdatePasswordDialogOpen}
          title='E-mail enviado!'
          description='Se este endereo está cadastrado conosco, você receberá um e-mail para redefinir sua senha. Caso não o encontre em sua caixa de entrada, verifique no seu spam ou lixo eletrônico.'
          onConfirm={() => setIsUpdatePasswordDialogOpen(false)}
        />
        <AboutUsModal
          isOpen={isAboutUsModalOpen}
          setIsOpen={() => setIsAboutUsModalOpen(!isAboutUsModalOpen)}
        />
        <CustomListItemButton
          label='Alterar senha'
          onClick={() => {
            setOpenConfirmModal(true)
          }}
          endIcon={<LockOpenOutlined />}
        />
        <CustomListItemButton
          label='Sobre nós'
          onClick={() => setIsAboutUsModalOpen(!isAboutUsModalOpen)}
          endIcon={<InfoOutlined />}
        />
        <Link
          style={{ textDecoration: 'none' }}
          href={`mailto:${INFO_SA_EMAIL}`}
          target='_blank'
        >
          <CustomListItemButton
            label={`Contato: ${INFO_SA_EMAIL}`}
            endIcon={<EmailOutlined />}
          />
        </Link>
        <NavLinkBase to={FAQ} absolute>
          <CustomListItemButton
            label='FAQ'
            onClick={() => {}}
            endIcon={<HelpOutline />}
          />
        </NavLinkBase>
        <Link
          style={{ textDecoration: 'none' }}
          href='/privacy_policy_pt.html'
          target='_blank'
        >
          <CustomListItemButton
            label='Políticas de privacidade'
            endIcon={<LaunchOutlined />}
          />
        </Link>
        <Link
          style={{ textDecoration: 'none' }}
          href='/terms_and_conditions_pt.html'
          target='_blank'
        >
          <CustomListItemButton
            label='Termos e condições'
            endIcon={<LaunchOutlined />}
          />
        </Link>
        <NavLinkBase to={LEGAL_INFORMATION} absolute>
          <CustomListItemButton
            label='Informações legais'
            onClick={() => {}}
            endIcon={<GavelOutlined />}
          />
        </NavLinkBase>
      </Box>
      <ConfirmationModal
        onCancel={() => setOpenConfirmModal(false)}
        onConfirm={handleConfirmUpdatePwd}
        open={openConfirmModal}
        title='Deseja trocar sua senha?'
      ></ConfirmationModal>
    </Box>
  )
}

export default ProfilePage
